import { Injectable } from '@angular/core';

import { tap, catchError } from 'rxjs/operators';
import { State, Action, StateContext } from '@ngxs/store';

import { BcStateContext } from '@bayclubs/state';

import { CoreFacade } from '@core/core.module';

import {
    BlockedSlotEventDetailsModel,
    GroupClassEventDetailsModel,
    CourtBookingEventDetailsModel,
    CourtEventService,
    BlockedSlotService,
    CourtBookingService
} from '@bayclubs/court-booking-employee-api';

import { COURT_EVENT_DETAILS_FEATURE_KEY, CourtEventDetailsStateModel } from './court-event-details.state.model';
import * as actions from './court-event-details.actions';

const initialState = {
    blockedSlotDetails: BcStateContext.Empty<BlockedSlotEventDetailsModel>(),
    courtClassDetails: BcStateContext.Empty<GroupClassEventDetailsModel>(),
    courtBookingDetails: BcStateContext.Empty<CourtBookingEventDetailsModel>()
} as CourtEventDetailsStateModel;

@Injectable({ providedIn: 'root' })
@State({ name: COURT_EVENT_DETAILS_FEATURE_KEY, defaults: initialState })
export class CourtEventDetailsState {
    constructor(
        private courtEventApiService: CourtEventService,
        private blockedSlotService: BlockedSlotService,
        private courtBookingService: CourtBookingService,
        private coreFacade: CoreFacade
    ) {}

    @Action(actions.LoadBlockedSlotDetailsAction)
    loadBlockedSlotDetails(ctx: StateContext<CourtEventDetailsStateModel>, action: actions.LoadBlockedSlotDetailsAction) {
        ctx.patchState({ blockedSlotDetails: BcStateContext.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtEventApiService
            .api10ClubIdCourteventsCourtEventIdBlockedslotGet({ clubId: club.id, courtEventId: action.courtEventId })
            .pipe(
                tap(value => {
                    ctx.patchState({ blockedSlotDetails: BcStateContext.Loaded(value) });
                }),
                catchError(err => {
                    ctx.patchState({ blockedSlotDetails: BcStateContext.Error() });
                    throw err;
                })
            );
    }

    @Action(actions.ClearLoadedBlockedSlotDetailsAction)
    clearLoadedBlockedSlotDetails(ctx: StateContext<CourtEventDetailsStateModel>) {
        ctx.patchState({ blockedSlotDetails: BcStateContext.Empty() });
    }

    @Action(actions.LoadCourtClassDetailsAction)
    loadCourtClassDetails(ctx: StateContext<CourtEventDetailsStateModel>, action: actions.LoadCourtClassDetailsAction) {
        ctx.patchState({ courtClassDetails: BcStateContext.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtEventApiService
            .api10ClubIdCourteventsCourtEventIdGroupclassGet({ clubId: club.id, courtEventId: action.courtEventId })
            .pipe(
                tap(value => {
                    ctx.patchState({ courtClassDetails: BcStateContext.Loaded(value) });
                }),
                catchError(err => {
                    ctx.patchState({ courtClassDetails: BcStateContext.Error() });
                    throw err;
                })
            );
    }

    @Action(actions.ClearLoadedCourtClassDetailsAction)
    clearLoadedCourtClassDetails(ctx: StateContext<CourtEventDetailsStateModel>) {
        ctx.patchState({ courtClassDetails: BcStateContext.Empty() });
    }

    @Action(actions.LoadCourtBookingDetailsAction)
    loadCourtBookingDetails(ctx: StateContext<CourtEventDetailsStateModel>, action: actions.LoadCourtBookingDetailsAction) {
        ctx.patchState({ courtBookingDetails: BcStateContext.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtEventApiService
            .api10ClubIdCourteventsCourtEventIdCourtbookingGet({ clubId: club.id, courtEventId: action.courtEventId })
            .pipe(
                tap(value => {
                    ctx.patchState({ courtBookingDetails: BcStateContext.Loaded(value) });
                }),
                catchError(err => {
                    ctx.patchState({ courtBookingDetails: BcStateContext.Error() });
                    throw err;
                })
            );
    }

    @Action(actions.ClearLoadedCourtBookingDetailsAction)
    clearLoadedCourtBookingDetails(ctx: StateContext<CourtEventDetailsStateModel>) {
        ctx.patchState({ courtClassDetails: BcStateContext.Empty() });
    }

    @Action(actions.NoShowAllCourtBookingDetailsAction)
    noShowAllCourtBooking(ctx: StateContext<CourtEventDetailsStateModel>, action: actions.NoShowAllCourtBookingDetailsAction) {
        const club = this.coreFacade.selectedClub();

        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdNoShowAllPost({
                clubId: club.id,
                courtBookingId: action.courtEventId
            })
            .pipe(
                tap(value => {
                    ctx.dispatch(new actions.LoadCourtBookingDetailsAction(action.courtEventId));
                }),
                catchError(err => {
                    throw err;
                })
            );
    }

    @Action(actions.CheckInAllCourtBookingDetailsAction)
    checkInAllCourtBooking(
        ctx: StateContext<CourtEventDetailsStateModel>,
        action: actions.CheckInAllCourtBookingDetailsAction
    ) {
        const club = this.coreFacade.selectedClub();

        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdCheckinAllPost({
                clubId: club.id,
                courtBookingId: action.courtEventId
            })
            .pipe(
                tap(value => {
                    ctx.dispatch(new actions.LoadCourtBookingDetailsAction(action.courtEventId));
                }),
                catchError(err => {
                    throw err;
                })
            );
    }

    @Action(actions.CheckInPlayerCourtBookingDetailsAction)
    checkInPlayerCourtBooking(
        ctx: StateContext<CourtEventDetailsStateModel>,
        action: actions.CheckInPlayerCourtBookingDetailsAction
    ) {
        const club = this.coreFacade.selectedClub();

        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdCheckinPersonIdPost({
                clubId: club.id,
                courtBookingId: action.courtEventId,
                personId: action.personId
            })
            .pipe(
                tap(value => {
                    ctx.dispatch(new actions.LoadCourtBookingDetailsAction(action.courtEventId));
                }),
                catchError(err => {
                    throw err;
                })
            );
    }
}
